import { BrainCircuit, CheckCircle2, Cloud, CreditCard, Globe, Mail, RefreshCcw, ShieldCheck, TriangleAlert, Users } from "lucide-react";
import { MissionControlActivityLog } from "@/components/admin/MissionControlActivityLog";
import { MissionControlCloserDesk } from "@/components/admin/MissionControlCloserDesk";
import { MissionControlEnvironmentPanel } from "@/components/admin/MissionControlEnvironmentPanel";

const statusStyles = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
  test: "border-amber-200 bg-amber-50 text-amber-700",
  attention: "border-amber-200 bg-amber-50 text-amber-700",
  pending: "border-slate-200 bg-slate-100 text-slate-600",
};

const iconMap = {
  domain: Globe,
  ssl: ShieldCheck,
  stripe: CreditCard,
  sync: RefreshCcw,
  cloudinary: Cloud,
  email: Mail,
  "lead-intelligence": BrainCircuit,
  "founder-seats": Users,
  "marketplace-hygiene": ShieldCheck,
};

const ChecklistCard = ({ card, testId }) => {
  const Icon = iconMap[card.key] || CheckCircle2;
  const badgeClass = statusStyles[card.status] || statusStyles.pending;
  const badgeLabel = card.status === "test" ? "test mode" : card.status;

  return (
    <div className="rounded-[28px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-[#fff4db] p-3 text-[#8b6d16]">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">{card.label}</p>
            <p className="mt-2 text-2xl font-semibold text-[#001935]">{card.value}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ${badgeClass}`} data-testid={`${testId}-status`}>
          {card.status === "ready" ? <CheckCircle2 className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
          {badgeLabel}
        </span>
      </div>
      <p className="mt-4 text-sm leading-7 text-[#42566f]">{card.detail}</p>
      {card.meta ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#6b6252]">{card.meta}</p> : null}
    </div>
  );
};

export const AdminLaunchChecklistPanel = ({ checklist, onEnvironmentModeChange, onSendPreview, sendingPreviewAgentId }) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const protocol = typeof window !== "undefined" ? window.location.protocol.replace(":", "") : "";

  const domainCard = {
    key: "domain",
    label: "Domain",
    status: hostname === "sgpropportal.com" || hostname === "www.sgpropportal.com" ? "ready" : "attention",
    value: hostname || "Unavailable",
    detail: "Target launch domains are sgpropportal.com and www.sgpropportal.com.",
    meta: origin || "No browser origin detected",
  };

  const sslCard = {
    key: "ssl",
    label: "SSL / HTTPS",
    status: protocol === "https" ? "ready" : "attention",
    value: protocol === "https" ? "HTTPS active" : protocol ? `${protocol.toUpperCase()} detected` : "Unknown",
    detail: protocol === "https" ? "Secure transport is active for the current session." : "Open this admin page on the secure production domain to confirm SSL.",
    meta: checklist?.checked_at ? `Backend checked ${checklist.checked_at}` : undefined,
  };

  const cards = [domainCard, sslCard, ...(checklist?.cards || [])];

  return (
    <section className="space-y-6" data-testid="admin-mission-control-panel">
      <div className="rounded-[32px] border border-[#D4AF37]/30 bg-[#fffaf1] p-6 shadow-[0_22px_70px_rgba(0,25,53,0.08)] md:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="mission-control-eyebrow">Mission Control</p>
        <h2 className="mt-3 text-3xl font-semibold text-[#001935]" data-testid="mission-control-heading">Go-live checklist at a glance</h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-[#42566f]">
          Use this panel as the single source of truth for SG Prop Portal launch readiness. It brings the current domain, HTTPS state, Stripe mode, Resend domain health, AI handshake status, founder seat occupancy, and marketplace sync into one admin view.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <ChecklistCard card={card} key={card.key} testId={`launch-checklist-card-${card.key}`} />
        ))}
      </div>

      <MissionControlEnvironmentPanel defaultMode={checklist?.environment_mode} onModeChange={onEnvironmentModeChange} productionSlots={checklist?.production_slots || []} twilioWebhookPath={checklist?.twilio_webhook_path} />

      <MissionControlCloserDesk deliverySummary={checklist?.lead_delivery_summary} onSendPreview={onSendPreview} rows={checklist?.closer_rows || []} sendingAgentId={sendingPreviewAgentId} />

      <MissionControlActivityLog logs={checklist?.activity_logs || []} />
    </section>
  );
};